'use client';

import {type FC, useCallback, useEffect, useRef, useState} from 'react';
import {PauseIcon, PlayIcon} from '@heroicons/react/20/solid';
import VimeoPlayer from '@vimeo/player';
import clsx from 'clsx';

import {usePrefersReducedMotion} from '@/hooks/usePrefersReducedMotion';

import type {MediaFragment} from '../../schema/primitives/media';

import type {MaskVariant} from './shared';
import {maskToAspectRatio, MEDIA_MASK_NONE} from './shared';

interface Props {
	video?: MediaFragment['video'];
	itemGalleryFirstMask?: MaskVariant;
	disabledMask?: boolean;
	disabledControls?: boolean;
}

export const MediaVideo: FC<Props> = ({
	video,
	itemGalleryFirstMask,
	disabledMask,
	disabledControls,
}) => {
	const [{height, width}, setDimensions] = useState({
		height: 0,
		width: 0,
	});
	const [playing, setPlaying] = useState(false);
	const [player, setPlayer] = useState<VimeoPlayer | null>(null);
	const [hiddenPause, setHiddenPause] = useState(false);
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const autoplay = video?.autoplay;
	const hasReducedMotionPreference = usePrefersReducedMotion();
	const videoParams = new URLSearchParams({
		autoplay:
			(autoplay || disabledControls) && !hasReducedMotionPreference
				? '1'
				: '0',
		loop:
			(autoplay || disabledControls) && !hasReducedMotionPreference
				? '1'
				: '0',
		autopause:
			(autoplay || disabledControls) && !hasReducedMotionPreference
				? '0'
				: '1',
		title: '0',
		byline: '0',
		portrait: '0',
		controls: '0',
		muted: '1',
		fullscreen: '1',
	});

	// Initialize Vimeo player
	useEffect(() => {
		if (!iframeRef.current) {
			return;
		}
		const player = new VimeoPlayer(iframeRef.current);
		setPlayer(player);
	}, [iframeRef]);

	// Play/pause video when clicking on the play button
	const onControlClick = useCallback(() => {
		if (playing) {
			player?.pause();
			setPlaying(false);
		} else {
			player?.play();
			setPlaying(true);
		}
	}, [player, playing]);

	// The pause icon should be hidden when the video is playing
	useEffect(() => {
		if (!playing) {
			return;
		}
		const timeout = setTimeout(() => {
			setHiddenPause(true);
		}, 1000);
		return () => {
			clearTimeout(timeout);
			setHiddenPause(false);
		};
	}, [playing]);

	// Fetch video dimensions from Vimeo API
	useEffect(() => {
		if (!video?.videoId) {
			return;
		}

		const getVideoDimensions = async () => {
			try {
				const response = await fetch(
					`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${video.videoId}`,
				);
				if (!response.ok) {
					throw new Error('Could not fetch video dimensions');
				}

				const dimensions = await response.json();
				setDimensions(dimensions);
			} catch (error) {
				console.error(error);
				setDimensions({height: 0, width: 0});
			}
		};

		getVideoDimensions();
	}, [video?.videoId]);

	// The video needs to fit within the mask and be cropped to the mask's aspect ratio
	// To do this, first we calculate the aspect ratio of the mask and the video
	// Then we compare the two ratios and set the height or width
	// - whichever is the dimension that shouldn't overflow
	// Then we calculate the other dimension
	// - from the aspect ratio of the video we can calculate the size relative to
	// the other dimension
	// - using the aspect ratio of the mask we can then get the size relative to
	// the relevant dimension
	// When there is no mask, the parent element is used for calculations
	const containerRef = useRef<HTMLDivElement>(null);
	const [parentWidth, setParentWidth] = useState(200);
	const [parentHeight, setParentHeight] = useState(200);
	useEffect(() => {
		if (!containerRef.current) {
			return;
		}
		const onResize = () => {
			setParentWidth(
				containerRef.current?.parentElement?.scrollWidth ?? 200,
			);
			setParentHeight(
				containerRef.current?.parentElement?.scrollHeight ?? 200,
			);
		};

		onResize();
		window.addEventListener('resize', onResize);
		return () => {
			window.removeEventListener('resize', onResize);
		};
	}, [containerRef, height, width, iframeRef]);

	const mask =
		!disabledMask &&
		(itemGalleryFirstMask ? itemGalleryFirstMask : video?.mask);
	const hasMask = mask && mask !== MEDIA_MASK_NONE && mask !== 'original';
	const maskAspect = hasMask
		? maskToAspectRatio(mask)
		: {width: parentWidth, height: parentHeight};
	const maskAspectRatio = maskAspect.width / maskAspect.height;
	const videoAspectRatio = width / height;
	const style = {
		height: '100%',
		width: '100%',
	};
	if (videoAspectRatio > maskAspectRatio) {
		const widthPercentRelativeToHeight = (100 * width) / height;
		const widthPercent =
			(widthPercentRelativeToHeight * maskAspect.height) /
			maskAspect.width;
		style.width = `${Math.ceil(widthPercent)}%`;
	} else if (videoAspectRatio < maskAspectRatio) {
		const heightPercentRelativeToWidth = (100 * height) / width;
		const heightPercent =
			(heightPercentRelativeToWidth * maskAspect.width) /
			maskAspect.height;
		style.height = `${Math.ceil(heightPercent)}%`;
	}

	return (
		<div
			className={clsx(
				'relative',
				'max-w-full',
				'max-h-full',
				'flex',
				'justify-center',
				'items-center',
				'overflow-hidden',
				mask,
			)}
			style={{
				aspectRatio: hasMask
					? `${maskAspect.width}/${maskAspect.height}`
					: parentWidth && parentHeight
						? `${parentWidth}/${parentHeight}`
						: undefined,
			}}
			ref={containerRef}
		>
			{video?.videoId && (
				<iframe
					name="vimeo-player"
					title="vimeo-player"
					src={`https://player.vimeo.com/video/${
						video.videoId
					}?${videoParams.toString()}`}
					allow="autoplay; muted;"
					className={clsx('absolute', 'pointer-events-none')}
					style={style}
					ref={iframeRef}
					tabIndex={disabledControls ? -1 : undefined}
				></iframe>
			)}

			{!autoplay && !disabledControls && (
				<button
					className={clsx(
						'absolute',
						'h-full',
						'w-full',
						'flex',
						'items-center',
						'justify-center',
						'group',
					)}
					onClick={onControlClick}
				>
					{playing ? (
						<PauseIcon
							className={clsx(
								'absolute',
								'h-14',
								'w-14',
								'text-turquoise-700',
								hiddenPause ? 'opacity-0' : 'opacity-100',
								'transition-all',
								'duration-300',
								'p-2',
								'rounded-full',
								'group-focus-visible:ring-2',
								'group-focus-visible:ring-turquoise-700',
							)}
						/>
					) : (
						<PlayIcon
							className={clsx(
								'absolute',
								'h-14',
								'w-14',
								'text-turquoise-700',
								'p-2',
								'rounded-full',
								'group-focus-visible:ring-2',
								'group-focus-visible:ring-turquoise-700',
							)}
						/>
					)}
				</button>
			)}
		</div>
	);
};
